// Refreshes the seat blocking page's statuses every few seconds (IDV-705): paused while the browser tab is hidden and
// refreshed as soon as it is shown, held while `busy` (a drag selection, a block in flight), never two at once.
// A refresh asked for while busy (the button, the tab shown again) waits for the next tick that isn't.
import { computed, reactive, ref, type Ref } from 'vue'

export interface PollingOptions {
  refresh: () => Promise<void>
  seconds?: number
  /** True while a refresh due now must wait */
  busy?: () => boolean
}

/** How often the clock checks whether a refresh is due, and moves "Atualizado há N s" */
const TICK_MS = 500

export function usePolling({ refresh, seconds = 10, busy = () => false }: PollingOptions) {
  const now: Ref<number> = ref(Date.now())
  const lastAt: Ref<number> = ref(Date.now())
  const paused: Ref<boolean> = ref(false)
  const running: Ref<boolean> = ref(false)
  const failed: Ref<boolean> = ref(false)
  /** A refresh was asked for while busy */
  let pending = false
  let inFlight: Promise<void> = Promise.resolve()
  let timer: ReturnType<typeof setInterval> | undefined

  /** Whole seconds since the last refresh ended */
  const elapsed = computed<number>(() => Math.max(0, Math.floor((now.value - lastAt.value) / 1000)))

  async function run() {
    running.value = true
    pending = false
    try {
      await refresh()
      failed.value = false
    } catch {
      failed.value = true
    } finally {
      lastAt.value = Date.now()
      now.value = lastAt.value
      running.value = false
    }
  }

  /** Refreshes now, or as soon as nothing holds it */
  function refreshNow(): Promise<void> {
    if (running.value) return inFlight
    if (busy()) {
      pending = true
      return Promise.resolve()
    }

    inFlight = run()
    return inFlight
  }

  /** Resolves once the refresh in flight, if any, has landed: call it before changing statuses */
  function settle(): Promise<void> {
    return inFlight
  }

  function tick() {
    now.value = Date.now()
    const due = pending || now.value - lastAt.value >= seconds * 1000
    if (!paused.value && !busy() && !running.value && due) void refreshNow()
  }

  function onVisibility() {
    paused.value = document.hidden
    if (!paused.value) void refreshNow()
  }

  function start() {
    stop()
    paused.value = document.hidden
    restart()
    timer = setInterval(tick, TICK_MS)
    document.addEventListener('visibilitychange', onVisibility)
  }

  function stop() {
    clearInterval(timer)
    timer = undefined
    document.removeEventListener('visibilitychange', onVisibility)
  }

  /** The statuses were just fetched some other way (after a block): the next refresh is a full interval away */
  function restart() {
    lastAt.value = Date.now()
    now.value = lastAt.value
  }

  return reactive({
    seconds,
    elapsed,
    paused,
    running,
    failed,
    start,
    stop,
    restart,
    refreshNow,
    settle,
  })
}

export type Polling = ReturnType<typeof usePolling>
